import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { generatePath } from 'react-router';
import { showMessage } from '~/actions';
import { logCustomEvent } from '~/api/analytics';
import browserHistory from '~/browserHistory';
import { showErrorMessage } from '~/domain/utils';
import { routes } from '~/routes';
import { apiMethods } from '~/services/api';
import { apiMethodsConst } from '~/services/methods';
import { jobKeys } from '../jobKeys';

export interface ICancelJobPayload {
  jobId: string;
  redirect?: boolean;
}

type TCancelJobResponse = {
  isOnsiteInterview?: boolean;
  [key: string]: unknown;
};
type TCancelJobError = AxiosError<{ message?: string; error?: string; title?: string }>;

/**
 * Cancel-job mutation. Owns the cancel API request, `cancel_job` analytics,
 * targeted cache invalidation, and the two success branches selected by the
 * `redirect` input: redirect navigates to the cancellation feedback screen,
 * while the non-redirect path shows the global success message popup. Errors
 * surface through the shared `showErrorMessage` helper.
 */
export function useCancelJobMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<TCancelJobResponse, TCancelJobError, ICancelJobPayload>({
    mutationFn: async ({ jobId }) => {
      return await apiMethods[apiMethodsConst.CANCEL_JOB_REQUEST]({ jobId });
    },
    onSuccess: (response, { jobId, redirect }) => {
      void logCustomEvent('cancel_job', { jobId });

      queryClient.invalidateQueries({ queryKey: ['dashboard-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-jobs-info'] });
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });

      if (redirect) {
        browserHistory.push(generatePath(routes.cancellationFeedback, { jobId }), {
          isOnsiteInterview: response?.isOnsiteInterview,
        });
        return;
      }

      dispatch(
        showMessage({
          title: 'Cancellation Confirmed',
          message: response?.isOnsiteInterview
            ? 'This onsite interview has been successfully cancelled.'
            : 'This shift has been successfully cancelled.',
          isSuccess: true,
        }),
      );
    },
    onError: (error) => {
      showErrorMessage({ error });
    },
  });
}
